<template>
  <div class="content">
    <h1 class="has-text-centered">Elektrische Ladung berechnen</h1>

    <div class="has-text-centered" v-if="calculators.length > 1">
      <label class="label">Welche Formel möchtest du benutzen?</label>
      <div class="field pb-2">
        <div class="dropdown is-300px-width" :class="{ 'is-active': dropdown_active }">
          <div class="dropdown-trigger is-300px-width">
            <button class="button is-fullwidth" @click="toggleDropdown()">
              <span v-if="target_calculator.title != ''">{{ target_calculator.title }}</span>
              <span v-else v-katex="target_calculator.katex_formula"></span>
              <span class="icon is-small">
                <i class="fas fa-angle-down" aria-hidden="true"></i>
              </span>
            </button>
          </div>
          <div class="dropdown-menu is-300px-width">
            <div class="dropdown-content">
              <div class="dropdown-item is-clickable" v-for="(calculator, index) in calculators" :key="index" @click="selectTargetCalculator(index)" v-show="index != target_calculator_index">
                <p v-if="calculator.title != ''">{{ calculator.title }}</p>
                <p v-else v-katex="calculator.katex_formula"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DefaultCalculator v-for="(calculator, index) in calculators" :key="index" :prop_katex_formula="calculator.katex_formula" :prop_parser_formula="calculator.parser_formula" :prop_variables="calculator.variables" :prop_id="calculator.id" :prop_target_calculator_index="target_calculator_index" :prop_target_variable="getTargetVariable()" v-show="target_calculator_index == index" />

    <FeedbackBox />

    <GeneralAffiliateBox />

    <div class="box">
      <h2>Was ist die elektrische Ladung?</h2>
      <p>
        Jede Materie in diesem Universum besteht aus elektrisch neutralen Atomen. Das liegt daran, dass jedes Atom die gleiche Anzahl von positiv geladenen Protonen und negativ geladenen Elektronen
        hat. Die Protonen befinden sich zusammen mit den elektrisch neutralen Neutronen im Atomkern. Und die Elektronen drehen sich in einer bestimmten Umlaufbahn um den Kern.
      </p>
      <p>
        Es ist mögliche manche Elektronen aus ihrer Umlaufbahn zu entfernen. Das führt dazu, dass die positiv geladenen Protonen überwiegen und das Atom dadurch insgesamt positiv geladen wird. Es ist
        ist auch möglich weitere Elektronen dieser Umlaufbahn hinzuzufügen. Das hat den umgekehrten Effekt: Die negativ geladenen Elektronen überwiegen und das Atom wird dadurch negativ geladen.
      </p>
      <p>
        Die elektrische Ladung ist das Ergebnis der Trennung von Elektronen und Protonen. Sie
        <strong>gibt den Elektronenüberschuss oder -mangel eines Körpers an und wird in Coulomb gemessen</strong>.
        Dabei entspricht ein Coulomb der elektrischen Ladung, die durch den Querschnitt eines Drahts transportiert wird, indem ein elektrischer Strom mit einem Ampere für eine Sekunde fließt.
      </p>

      <h2>Wie berechnet man die elektrische Ladung?</h2>
      <p>
        Die elektrische Ladung lässt sich mithilfe folgender Formeln berechnen:
      </p>
      <FormulaDescription v-for="(calculator, index) in calculators" :key="index" :katex_formula="calculator.katex_formula" :variables="calculator.variables" />

      <h2>Weiterführende Links</h2>
      <p>
        <ul class="fa-ul pb-3">
          <li>
            <span class="fa-li"><i class="fas fa-arrow-circle-right"></i></span>
            <a href="https://de.wikipedia.org/wiki/Elektrische_Ladung" target="_blank">Wikipedia-Artikel</a>
          </li>
        </ul>
      </p>

    </div>
  </div>
</template>

<script>
import DefaultCalculator from '@/components/DefaultCalculator.vue'
import FormulaDescription from '@/components/FormulaDescription.vue'
import FeedbackBox from '@/components/FeedbackBox.vue'
import GeneralAffiliateBox from '@/components/GeneralAffiliateBox.vue'

export default {
  name: 'calculator-electric-charge',
  components: {
    DefaultCalculator,
    FormulaDescription,
    FeedbackBox,
    GeneralAffiliateBox
  },
  metaInfo: {
    title: 'Elektrische Ladung einfach online berechnen',
    meta: [
      {
        name: 'description',
        content: 'Die elektrische Ladung gibt den Elektronenüberschuss oder -mangel eines Körpers an und wird in Coulomb gemessen. Hier einfach online berechnen.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'calculators' })
    this.target_calculator_index = this.getTargetCalculator()
  },
  methods: {
    toggleDropdown () {
      if (this.dropdown_active) {
        this.dropdown_active = false
      } else {
        this.dropdown_active = true
      }
    },
    selectTargetCalculator (index) {
      this.target_calculator_index = index
      this.dropdown_active = false
    },
    getValue (calcId, variable) {
      variable = variable.toLowerCase() + '_val'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return this.$route.query[key]
          }
        }
      }
      return 1
    },
    getMultiplicator (calcId, variable) {
      variable = variable.toLowerCase() + '_mul'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return Math.pow(10, this.$route.query[key])
          }
        }
      }
      return 1
    },
    getTargetVariable () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          for (const key in this.$route.query) {
            if (key === 'target_var') {
              for (const y in this.calculators[i].variables) {
                if (this.calculators[i].variables[y].variable.toLowerCase() === this.$route.query[key]) {
                  return this.calculators[i].variables[y].label
                }
              }
            }
          }
        }
      }
      return null
    },
    getTargetCalculator () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          return parseInt(i)
        }
      }
      return 0
    }
  },
  computed: {
    target_calculator: function () {
      return this.calculators[this.target_calculator_index]
    }
  },
  data () {
    return {
      dropdown_active: false,
      target_calculator_index: 0,
      calculators: [
        {
          id: 1,
          title: '',
          katex_formula: 'Q = I*t',
          parser_formula: 'Q-I*t',
          variables: [
            {
              variable: 'Q',
              unit: {
                content: 'C',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'Q',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Ladung in Coloumb [C]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'Q'),
                unitMultiplicator: this.getMultiplicator(1, 'Q')
              },
              is_constant: false
            },
            {
              variable: 'I',
              unit: {
                content: 'A',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'I',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Stromstärke in Ampere [A]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'I'),
                unitMultiplicator: this.getMultiplicator(1, 'I')
              },
              is_constant: false
            },
            {
              variable: 't',
              unit: {
                content: 's',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 't',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Zeit in Sekunden [s]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 't'),
                unitMultiplicator: this.getMultiplicator(1, 't')
              },
              is_constant: false
            }
          ]
        },
        {
          id: 2,
          title: '',
          katex_formula: 'Q = n*e',
          parser_formula: 'Q-n*elad',
          variables: [
            {
              variable: 'Q',
              unit: {
                content: 'C',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'Q',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Ladung in Coloumb [C]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(2, 'Q'),
                unitMultiplicator: this.getMultiplicator(2, 'Q')
              },
              is_constant: false
            },
            {
              variable: 'n',
              unit: {
                content: '',
                type: 'text',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: 'n',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Anzahl der Elektronen',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(2, 'n'),
                unitMultiplicator: this.getMultiplicator(2, 'n')
              },
              is_constant: false
            },
            {
              variable: 'elad',
              unit: {
                content: 'C',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'e',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Elementarladung von 1.60217662018*10<sup>-19</sup> in Coloumb [C]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: 1.60217662018 * Math.pow(10, -19),
                unitMultiplicator: 1
              },
              is_constant: true
            }
          ]
        }
      ]
    }
  }
}
</script>
